/**
 * The 16-step sequencer grid — the centre of the Bandmate UI (SPEC §6 item 5).
 *
 *   const seq = mountSequencer(container, { store, engine });
 *   seq.setStep(step);   // engine playhead callback (also wired internally via engine.onStep)
 *   seq.dispose();
 *
 * Accessibility: ONE `role=grid` per track kind (drums: 6 lanes × 16; melodic:
 * 36 pitch rows × 16) with a roving tabindex (a single tab stop). Arrow keys
 * move, Enter toggles, Shift+Enter accents, Home/End jump within the row,
 * PageUp/PageDown move an octave on melodic tracks. Every cell is a
 * `role=gridcell` with `aria-label="Kick, bar 1, step 5, on"` and `aria-pressed`.
 *
 * Rendering: both grids are built ONCE on mount; the store subscriber only
 * flips `data-on`, `data-vel`, `data-by`, `data-tail`, `data-continues` and
 * the aria state. The playhead is one absolutely positioned column moved with
 * `transform: translateX(step * cellStride)` where the stride is measured from
 * the rendered cells (width + gap; jsdom falls back to `CELL_MAX_PX`). Agent
 * writes reveal with the per-step stagger from `reveal.ts` using
 * `ChangeMeta.agentSteps` (cells on the same step appear together).
 *
 * Note tails (`data-tail`) are labelled "…, held" with `aria-pressed=true`:
 * activating a tail removes the note that owns it.
 *
 * Interactions: click toggles a 1-step note/hit (velocity 0.8; Shift = 1.0
 * accent; clicking an "on" cell removes it); dragging paints on/off across the
 * row, coalesced into ONE `store.apply` and ONE `store.logHuman` per
 * pointerdown → pointerup. While `store.mode() === 'auditioning'` the grid is
 * `inert` behind a slim banner. While `'recording'` the grid stays focusable
 * (the piano's computer keys must not lose their focus target) but is
 * read-only (`data-readonly`, `aria-readonly`, banner): the DOM mirrors the
 * live transient buffer, so a click would edit a committed session that is
 * not what the human sees. Edits resume after Stop.
 */
import './sequencer.css';
import {
  ACCENT_THRESHOLD,
  BARS,
  DRUM_LANES,
  STEPS_PER_BAR,
  TOTAL_STEPS,
  TRACK_HOME_OCTAVE,
  TRACK_RANGE,
  joinStep,
  type Author,
  type DrumHit,
  type DrumLane,
  type Key,
  type MelodicTrackId,
  type Note,
  type Session,
  type TrackId,
} from '../types';
import type { ChangeMeta, DrumHitInput, Mutation, NoteInput, Store } from '../state/store-api';
import { h, pitchLabel, setFlag } from './dom';
import { revealCells } from './reveal';

/** The slice of the audio engine the sequencer needs (structurally satisfied by `audio/engine.ts`'s Engine). */
export interface SequencerEngine {
  previewNote(track: MelodicTrackId, pitch: number, velocity?: number): void;
  previewDrum(lane: DrumLane, velocity?: number): void;
  /** Called on every 16th with the absolute step (0..63); returns an unsubscribe. */
  onStep(fn: (step: number) => void): () => void;
  isPlaying(): boolean;
}

export interface SequencerDeps {
  store: Store;
  engine: SequencerEngine;
}

export interface SequencerHandle {
  /** Root element (`.bm-seq`). */
  el: HTMLElement;
  /** Move the playhead to absolute step `step` (0..63). Hidden when the step's bar is not the shown bar. */
  setStep(step: number): void;
  dispose(): void;
}

/** Velocity bucket shown through `data-vel`. */
export type VelocityClass = 'accent' | 'normal' | 'ghost';

/** Maximum cell edge in px (`min(100%/16, 34px)`); also the jsdom fallback for playhead maths. */
export const CELL_MAX_PX = 34;
/** Velocity written by a plain click / Enter. */
export const CLICK_VELOCITY = 0.8;
/** Velocity written by Shift+click / Shift+Enter. */
export const ACCENT_VELOCITY = 1.0;
/** Velocities below this render as `ghost`. */
export const GHOST_THRESHOLD = 0.65;

/** Display names for drum lanes, in `DRUM_LANES` order. */
export const LANE_LABELS: Readonly<Record<DrumLane, string>> = {
  kick: 'Kick',
  snare: 'Snare',
  clap: 'Clap',
  hat: 'Hat',
  openhat: 'Open hat',
  perc: 'Perc',
};

const TRACK_LABELS: Readonly<Record<TrackId, string>> = { drums: 'Drums', bass: 'Bass', chords: 'Chords', lead: 'Lead' };

const MAJOR_PCS = [0, 2, 4, 5, 7, 9, 11];
const MINOR_PCS = [0, 2, 3, 5, 7, 8, 10];
const TONIC_PC: Readonly<Record<Key['tonic'], number>> = {
  C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11,
};

/** Pitch classes (0..11) of the key's scale, computed locally so the UI stays theory-free. */
export function keyPitchClasses(key: Key): Set<number> {
  const root = TONIC_PC[key.tonic];
  const set = key.mode === 'major' ? MAJOR_PCS : MINOR_PCS;
  return new Set(set.map((pc) => (pc + root) % 12));
}

/** `accent` ≥ ACCENT_THRESHOLD, `ghost` < GHOST_THRESHOLD, else `normal`. */
export function velocityClass(v: number): VelocityClass {
  if (v >= ACCENT_THRESHOLD) return 'accent';
  if (v < GHOST_THRESHOLD) return 'ghost';
  return 'normal';
}

// ---------- internal grid model ----------

interface Row {
  el: HTMLElement;
  header: HTMLElement;
  cells: HTMLElement[];
  /** Drum lane or MIDI pitch, depending on the grid kind. */
  lane?: DrumLane;
  pitch?: number;
}

interface Grid {
  kind: 'drums' | 'melodic';
  el: HTMLElement;
  rows: Row[];
  playhead: HTMLElement;
  focus: { row: number; col: number };
  /** Melodic only: the track whose pitch window the rows currently show. */
  track?: MelodicTrackId;
  keyId?: string;
}

interface Paint {
  grid: Grid;
  row: number;
  /** True = paint on (write), false = erase. */
  on: boolean;
  accent: boolean;
  cols: Set<number>;
}

const STATE_ATTRS = ['data-on', 'data-tail', 'data-continues', 'data-vel', 'data-by'] as const;

/** Banner shown over the inert grid while an audition is open. */
export const AUDITION_BANNER = 'Auditioning — press ▶A ▶B, keep one to continue';
/** Banner shown while recording: the grid follows the live buffer and edits pause. */
export const RECORDING_BANNER = 'Recording — the grid follows your playing; edits resume after Stop';

/** Mount the sequencer into `container`. */
export function mountSequencer(container: HTMLElement, deps: SequencerDeps): SequencerHandle {
  const { store, engine } = deps;

  const banner = h('div', { class: 'bm-seq__banner', role: 'status', hidden: true });
  const scroller = h('div', { class: 'bm-seq__scroll' });
  const root = h('div', { class: 'bm-seq' }, banner, scroller);

  const drums = buildGrid('drums');
  const melodic = buildGrid('melodic');
  scroller.append(drums.el, melodic.el);

  let shownTrack: TrackId | null = null;
  let shownBar = -1;
  let lastStep = -1;
  let paint: Paint | null = null;

  // ----- building -----

  function buildGrid(kind: Grid['kind']): Grid {
    const rowCount = kind === 'drums' ? DRUM_LANES.length : TRACK_RANGE.lead[1] - TRACK_RANGE.lead[0] + 1;
    const rows: Row[] = [];
    const gridEl = h('div', { class: `bm-seq__grid bm-seq__grid--${kind}`, role: 'grid', hidden: true });
    for (let r = 0; r < rowCount; r++) {
      const header = h('div', { class: 'bm-seq__label', role: 'rowheader' });
      const cells: HTMLElement[] = [];
      const rowEl = h('div', { class: 'bm-seq__row', role: 'row' }, header);
      for (let c = 0; c < STEPS_PER_BAR; c++) {
        const cell = h('div', {
          class: 'bm-seq__cell',
          role: 'gridcell',
          tabindex: '-1',
          'aria-pressed': 'false',
          dataset: { col: c, beat: c % 4 === 0 ? '' : undefined },
        });
        cells.push(cell);
        rowEl.append(cell);
      }
      const row: Row = { el: rowEl, header, cells };
      if (kind === 'drums') {
        row.lane = DRUM_LANES[r]!;
        rowEl.dataset.lane = row.lane;
        header.textContent = LANE_LABELS[row.lane];
      }
      rows.push(row);
      gridEl.append(rowEl);
    }
    const playhead = h('div', { class: 'bm-seq__playhead', 'aria-hidden': 'true', hidden: true });
    gridEl.append(playhead);
    const grid: Grid = { kind, el: gridEl, rows, playhead, focus: { row: 0, col: 0 } };
    rows[0]!.cells[0]!.setAttribute('tabindex', '0');
    gridEl.addEventListener('keydown', (e) => onKey(grid, e));
    gridEl.addEventListener('pointerdown', (e) => onPointerDown(grid, e));
    gridEl.addEventListener('pointerover', (e) => paintOver(grid, e.target));
    gridEl.addEventListener('pointermove', (e) => onPointerMove(grid, e));
    gridEl.addEventListener('focusin', (e) => {
      const pos = cellPos(grid, e.target);
      if (pos) setRoving(grid, pos.row, pos.col);
    });
    return grid;
  }

  /** Re-label the melodic rows for `track` (pitch window) and `key` (diatonic tint, tonic mark). */
  function relabelMelodic(track: MelodicTrackId, key: Key): void {
    const high = TRACK_RANGE[track][1];
    const pcs = keyPitchClasses(key);
    const tonicPc = TONIC_PC[key.tonic];
    melodic.rows.forEach((row, i) => {
      const pitch = high - i;
      row.pitch = pitch;
      row.el.dataset.pitch = String(pitch);
      row.header.textContent = pitchLabel(pitch);
      setFlag(row.el, 'data-diatonic', pcs.has(((pitch % 12) + 12) % 12));
      setFlag(row.el, 'data-tonic', ((pitch % 12) + 12) % 12 === tonicPc);
      setFlag(row.el, 'data-octave', pitch % 12 === 0);
    });
    melodic.track = track;
    melodic.keyId = `${key.tonic}${key.mode}`;
  }

  /** Scroll the melodic grid so the track's home octave sits in the middle. */
  function centreHomeOctave(track: MelodicTrackId): void {
    const homePitch = (TRACK_HOME_OCTAVE[track] + 1) * 12 + 6;
    const row = melodic.rows.find((r) => r.pitch === homePitch) ?? melodic.rows[Math.floor(melodic.rows.length / 2)]!;
    const top = row.el.offsetTop - (scroller.clientHeight - row.el.offsetHeight) / 2;
    if (Number.isFinite(top)) scroller.scrollTop = Math.max(0, top);
  }

  // ----- rendering -----

  function cellLabel(grid: Grid, row: Row, col: number, bar: number, on: boolean): string {
    return `${rowName(grid, row)}, bar ${bar + 1}, step ${col + 1}, ${on ? 'on' : 'off'}`;
  }

  function markCell(cell: HTMLElement, velocity: number, by: Author): void {
    cell.setAttribute('data-on', '');
    cell.setAttribute('data-vel', velocityClass(velocity));
    cell.setAttribute('data-by', by);
    cell.setAttribute('aria-pressed', 'true');
  }

  /** Mark `cell` as part of a sustained note (never overrides a note start). */
  function markTail(grid: Grid, row: Row, col: number, bar: number, by: Author): void {
    const tail = row.cells[col]!;
    if (tail.hasAttribute('data-on')) return;
    tail.setAttribute('data-tail', '');
    tail.setAttribute('data-by', by);
    tail.setAttribute('aria-pressed', 'true');
    tail.setAttribute('aria-label', `${rowName(grid, row)}, bar ${bar + 1}, step ${col + 1}, held`);
  }

  function rowName(grid: Grid, row: Row): string {
    return grid.kind === 'drums' ? LANE_LABELS[row.lane!] : pitchLabel(row.pitch!);
  }

  function render(session: Session, meta?: ChangeMeta): void {
    const track = session.ui.selectedTrack;
    const bar = Math.min(BARS - 1, Math.max(0, session.ui.selectedBar));
    const grid = track === 'drums' ? drums : melodic;
    const other = grid === drums ? melodic : drums;

    if (track !== 'drums') {
      const keyId = `${session.key.tonic}${session.key.mode}`;
      const trackChanged = melodic.track !== track;
      if (trackChanged || melodic.keyId !== keyId) relabelMelodic(track, session.key);
      if (trackChanged) centreHomeOctave(track);
    }

    other.el.hidden = true;
    grid.el.hidden = false;
    grid.el.setAttribute('aria-label', `${TRACK_LABELS[track]} sequencer, bar ${bar + 1}`);

    // Reset every cell, then paint content for the shown bar.
    for (const row of grid.rows) {
      row.cells.forEach((cell, col) => {
        for (const a of STATE_ATTRS) cell.removeAttribute(a);
        cell.setAttribute('aria-pressed', 'false');
        cell.setAttribute('aria-label', cellLabel(grid, row, col, bar, false));
      });
    }

    if (grid.kind === 'drums') {
      const byLane = new Map<DrumLane, Row>(grid.rows.map((r) => [r.lane!, r]));
      for (const hit of session.tracks.drums.hits) {
        if (Math.floor(hit.step / STEPS_PER_BAR) !== bar) continue;
        const row = byLane.get(hit.lane);
        if (!row) continue;
        const col = hit.step % STEPS_PER_BAR;
        const cell = row.cells[col]!;
        markCell(cell, hit.velocity, hit.by);
        cell.setAttribute('aria-label', cellLabel(grid, row, col, bar, true));
      }
    } else {
      const byPitch = new Map<number, Row>(grid.rows.map((r) => [r.pitch!, r]));
      const notes = session.tracks[track as MelodicTrackId].notes;
      for (const note of notes) {
        const row = byPitch.get(note.pitch);
        if (!row) continue;
        const noteBar = Math.floor(note.step / STEPS_PER_BAR);
        const startCol = note.step % STEPS_PER_BAR;
        if (noteBar === bar) {
          const cell = row.cells[startCol]!;
          markCell(cell, note.velocity, note.by);
          cell.setAttribute('aria-label', cellLabel(grid, row, startCol, bar, true));
          const lastCol = Math.min(STEPS_PER_BAR - 1, startCol + note.length - 1);
          for (let c = startCol + 1; c <= lastCol; c++) markTail(grid, row, c, bar, note.by);
          if (startCol + note.length > STEPS_PER_BAR) row.cells[lastCol]!.setAttribute('data-continues', '');
        } else {
          // Spill-over from the previous bar (wrapping from bar 4 into bar 1).
          const spill = startCol + note.length - STEPS_PER_BAR;
          if (spill > 0 && (noteBar + 1) % BARS === bar) {
            for (let c = 0; c < Math.min(spill, STEPS_PER_BAR); c++) markTail(grid, row, c, bar, note.by);
          }
        }
      }
    }

    const mode = store.mode();
    const auditioning = mode === 'auditioning';
    const recording = mode === 'recording';
    setFlag(grid.el, 'inert', auditioning);
    setFlag(other.el, 'inert', auditioning);
    setFlag(grid.el, 'data-readonly', recording);
    grid.el.setAttribute('aria-readonly', recording ? 'true' : 'false');
    const bannerText = auditioning ? AUDITION_BANNER : recording ? RECORDING_BANNER : '';
    if (banner.textContent !== bannerText) banner.textContent = bannerText;
    banner.hidden = !bannerText;

    shownTrack = track;
    shownBar = bar;
    if (lastStep >= 0) setStep(lastStep);

    if (meta?.agentSteps && meta.agentSteps.track === track) {
      const wanted = new Set(meta.agentSteps.steps.filter((s) => Math.floor(s / STEPS_PER_BAR) === bar).map((s) => s % STEPS_PER_BAR));
      if (wanted.size) {
        // One reveal step per grid column: a chord's notes appear together.
        const groups: HTMLElement[][] = [];
        for (const col of [...wanted].sort((a, b) => a - b)) {
          const group = grid.rows.map((row) => row.cells[col]!).filter((cell) => cell.hasAttribute('data-on'));
          if (group.length) groups.push(group);
        }
        revealCells(groups);
      }
    }
  }

  // ----- playhead -----

  /**
   * Measure the rendered cells: `width` of one cell, `stride` from one column
   * to the next (width + gap) and the `left` edge of column 0 inside the grid.
   * Without layout (jsdom, display:none) every value falls back to
   * `CELL_MAX_PX` and `left` stays null (the stylesheet's `left` applies).
   */
  function measure(grid: Grid): { width: number; stride: number; left: number | null } {
    const c0 = grid.rows[0]?.cells[0];
    const c1 = grid.rows[0]?.cells[1];
    if (!c0 || typeof c0.getBoundingClientRect !== 'function') return { width: CELL_MAX_PX, stride: CELL_MAX_PX, left: null };
    const r0 = c0.getBoundingClientRect();
    if (!(r0.width > 0)) return { width: CELL_MAX_PX, stride: CELL_MAX_PX, left: null };
    const r1 = c1?.getBoundingClientRect();
    const stride = r1 && r1.left > r0.left ? r1.left - r0.left : r0.width;
    return { width: r0.width, stride, left: r0.left - grid.el.getBoundingClientRect().left };
  }

  function setStep(step: number): void {
    lastStep = step;
    const grid = shownTrack === 'drums' ? drums : melodic;
    const abs = ((Math.floor(step) % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
    const bar = Math.floor(abs / STEPS_PER_BAR);
    if (bar !== shownBar) {
      grid.playhead.hidden = true;
      return;
    }
    const { width, stride, left } = measure(grid);
    grid.playhead.style.width = `${width}px`;
    if (left !== null) grid.playhead.style.left = `${left}px`;
    grid.playhead.style.transform = `translateX(${(abs % STEPS_PER_BAR) * stride}px)`;
    grid.playhead.hidden = false;
  }

  // ----- keyboard -----

  function cellPos(grid: Grid, target: EventTarget | null): { row: number; col: number; cell: HTMLElement } | null {
    const cell = (target as HTMLElement | null)?.closest?.<HTMLElement>('[role=gridcell]') ?? null;
    if (!cell || !grid.el.contains(cell)) return null;
    const rowEl = cell.closest<HTMLElement>('[role=row]');
    const row = grid.rows.findIndex((r) => r.el === rowEl);
    const col = Number(cell.dataset.col);
    if (row < 0 || Number.isNaN(col)) return null;
    return { row, col, cell };
  }

  function setRoving(grid: Grid, row: number, col: number): void {
    const prev = grid.rows[grid.focus.row]?.cells[grid.focus.col];
    prev?.setAttribute('tabindex', '-1');
    grid.focus = { row, col };
    grid.rows[row]!.cells[col]!.setAttribute('tabindex', '0');
  }

  function moveFocus(grid: Grid, row: number, col: number): void {
    row = Math.max(0, Math.min(grid.rows.length - 1, row));
    col = Math.max(0, Math.min(STEPS_PER_BAR - 1, col));
    setRoving(grid, row, col);
    const cell = grid.rows[row]!.cells[col]!;
    cell.focus();
    cell.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }

  function onKey(grid: Grid, e: KeyboardEvent): void {
    const pos = cellPos(grid, e.target);
    if (!pos) return;
    const { row, col } = pos;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        moveFocus(grid, row, col + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveFocus(grid, row, col - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveFocus(grid, row + 1, col);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocus(grid, row - 1, col);
        break;
      case 'Home':
        e.preventDefault();
        moveFocus(grid, row, 0);
        break;
      case 'End':
        e.preventDefault();
        moveFocus(grid, row, STEPS_PER_BAR - 1);
        break;
      case 'PageUp':
        if (grid.kind === 'melodic') {
          e.preventDefault();
          moveFocus(grid, row - 12, col);
        }
        break;
      case 'PageDown':
        if (grid.kind === 'melodic') {
          e.preventDefault();
          moveFocus(grid, row + 12, col);
        }
        break;
      case 'Enter':
        e.preventDefault();
        commitCells(grid, row, [col], e.shiftKey, null);
        break;
      case ' ':
        // Space is play/stop at the app level; keep the page from scrolling.
        e.preventDefault();
        break;
      default:
        return;
    }
  }

  // ----- pointer painting -----

  function isCellOn(grid: Grid, row: number, col: number): boolean {
    const cell = grid.rows[row]!.cells[col]!;
    return cell.hasAttribute('data-on') || cell.hasAttribute('data-tail');
  }

  function onPointerDown(grid: Grid, e: PointerEvent): void {
    if (e.button !== 0) return;
    const pos = cellPos(grid, e.target);
    if (!pos) return;
    e.preventDefault();
    setRoving(grid, pos.row, pos.col);
    const cell = grid.rows[pos.row]!.cells[pos.col]!;
    // Cancelling pointerdown can suppress the default focus change; keep the
    // roving tab stop and the real focus in sync so the arrow keys continue here.
    if (document.activeElement !== cell) cell.focus({ preventScroll: true });
    if (store.mode() !== 'idle') return; // read-only while recording (inert while auditioning)
    const on = isCellOn(grid, pos.row, pos.col);
    const accent = e.shiftKey;
    const upgrade = accent && on && cell.getAttribute('data-vel') !== 'accent';
    paint = { grid, row: pos.row, on: !on || upgrade, accent, cols: new Set([pos.col]) };
    if (paint.on) previewCell(grid, pos.row, accent ? ACCENT_VELOCITY : CLICK_VELOCITY);
    document.addEventListener('pointerup', onPointerUp, { once: true });
    document.addEventListener('pointercancel', onPointerUp, { once: true });
  }

  /**
   * Touch pointers are implicitly captured by the pointerdown target, so
   * `pointerover` never reaches the cells being dragged across; resolve the
   * cell under the finger/mouse from its coordinates instead.
   */
  function onPointerMove(grid: Grid, e: PointerEvent): void {
    if (!paint || paint.grid !== grid) return;
    const hit = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(e.clientX, e.clientY) : null;
    paintOver(grid, hit ?? e.target);
  }

  function paintOver(grid: Grid, target: EventTarget | null): void {
    if (!paint || paint.grid !== grid) return;
    const pos = cellPos(grid, target);
    if (!pos || pos.row !== paint.row) return;
    if (paint.cols.has(pos.col)) return;
    // Only paint cells that need changing in the paint direction.
    if (isCellOn(grid, pos.row, pos.col) === paint.on && !(paint.on && paint.accent)) return;
    paint.cols.add(pos.col);
    if (paint.on) previewCell(grid, pos.row, paint.accent ? ACCENT_VELOCITY : CLICK_VELOCITY);
  }

  function onPointerUp(): void {
    if (!paint) return;
    const p = paint;
    paint = null;
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerUp);
    commitCells(p.grid, p.row, [...p.cols].sort((a, b) => a - b), p.accent, p.on);
  }

  function previewCell(grid: Grid, row: number, velocity: number): void {
    const r = grid.rows[row]!;
    if (grid.kind === 'drums') engine.previewDrum(r.lane!, velocity);
    else if (shownTrack && shownTrack !== 'drums') engine.previewNote(shownTrack, r.pitch!, velocity);
  }

  // ----- committing to the store -----

  /**
   * Write or erase `cols` in `row` of the shown bar as ONE mutation.
   * `forceOn` = null lets a single toggle decide from the cell state.
   */
  function commitCells(grid: Grid, row: number, cols: number[], accent: boolean, forceOn: boolean | null): void {
    if (!cols.length || shownTrack === null || store.mode() !== 'idle') return;
    const bar = shownBar;
    const r = grid.rows[row]!;
    const session = store.get();
    const velocity = accent ? ACCENT_VELOCITY : CLICK_VELOCITY;
    const single = cols.length === 1;
    const firstOn = isCellOn(grid, row, cols[0]!);
    const cell = r.cells[cols[0]!]!;
    const upgrade = accent && firstOn && cell.getAttribute('data-vel') !== 'accent';
    const on = forceOn ?? (!firstOn || upgrade);
    const steps = cols.map((c) => joinStep(bar, c));
    const name = grid.kind === 'drums' ? r.lane! : pitchLabel(r.pitch!);
    const where = `b${bar + 1} s${cols[0]! + 1}`;

    let mutation: Mutation;
    let label: string;

    if (grid.kind === 'drums') {
      const lane = r.lane!;
      if (on) {
        const hits: DrumHitInput[] = steps.map((step) => ({ step, lane, velocity }));
        mutation = { type: 'setDrumHits', mode: 'merge', hits };
      } else {
        const keep: DrumHitInput[] = session.tracks.drums.hits
          .filter((x) => x.lane === lane && Math.floor(x.step / STEPS_PER_BAR) === bar && !steps.includes(x.step))
          .map(copyHit);
        mutation = { type: 'setDrumHits', mode: 'replace', bars: [bar], lanes: [lane], hits: keep };
      }
      label = single ? `toggled ${lane} ${where} ${on ? 'on' : 'off'}` : `${on ? 'painted' : 'cleared'} ${cols.length} ${lane} steps in bar ${bar + 1}`;
    } else {
      const track = shownTrack as MelodicTrackId;
      const pitch = r.pitch!;
      const notes = session.tracks[track].notes;
      if (on) {
        const written: NoteInput[] = steps.map((step) => {
          const existing = notes.find((n) => n.pitch === pitch && n.step === step);
          return { step, pitch, length: existing?.length ?? 1, velocity };
        });
        mutation = { type: 'writeNotes', track, mode: 'merge', notes: written };
      } else {
        const keep: NoteInput[] = notes.filter((n) => !(n.pitch === pitch && steps.some((s) => noteCovers(n, s)))).map(copyNote);
        mutation = { type: 'writeNotes', track, mode: 'replace', notes: keep };
      }
      label = single
        ? `toggled ${track} ${name} ${where} ${on ? 'on' : 'off'}`
        : `${on ? 'painted' : 'cleared'} ${cols.length} ${track} ${name} steps in bar ${bar + 1}`;
    }

    store.apply(mutation, { by: 'human', label });
    store.logHuman({ kind: single ? 'toggle_step' : 'paint_steps', detail: label.slice(0, 80) });
    // Pointer paints preview live as cells are painted; the keyboard path previews here.
    if (forceOn === null && on) previewCell(grid, row, velocity);
  }

  // ----- wiring -----

  const unsubscribe = store.subscribe((_s, meta) => render(store.current(), meta));
  const offStep = engine.onStep((step) => {
    if (engine.isPlaying()) {
      const bar = Math.floor(((step % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS / STEPS_PER_BAR);
      if (bar !== store.get().ui.selectedBar) store.apply({ type: 'select', bar }, { by: 'human', label: 'follow playback' });
    }
    setStep(step);
  });

  render(store.current());
  container.append(root);
  if (shownTrack && shownTrack !== 'drums') centreHomeOctave(shownTrack);

  return {
    el: root,
    setStep,
    dispose() {
      unsubscribe();
      offStep();
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
      paint = null;
      root.remove();
    },
  };
}

function copyHit(x: DrumHit): DrumHitInput {
  return { step: x.step, lane: x.lane, velocity: x.velocity, by: x.by };
}

function copyNote(n: Note): NoteInput {
  return { step: n.step, pitch: n.pitch, length: n.length, velocity: n.velocity, by: n.by };
}

/** True when absolute step `s` falls inside the note (start inclusive), allowing loop wrap. */
function noteCovers(n: Note, s: number): boolean {
  const end = n.step + n.length;
  return (s >= n.step && s < end) || (s + TOTAL_STEPS >= n.step && s + TOTAL_STEPS < end);
}
